<template>
    <div id="audience-selector" class="position-relative">
        <div id="audience-columns" class="row">
            <!-- Available List -->
            <div class="col-lg-6 col-12">
                <div class="card h-100 d-flex flex-column">
                    <div class="card-body pb-0">
                        <div class="row">
                            <div class="col-8 mb-2">
                                <h3>Available Audiences ({{ filtered_audience_list.length }})</h3>
                            </div>
                            <div class="col-4 text-end mb-2">
                                <a href="/admin/transformation_segments/create" class="btn btn-primary">
                                    <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add Audience
                                </a>
                            </div>
                        </div>
                        
                        <div class="search-box position-relative mb-3">
                            <i class="fa fa-search"></i>
                            <input type="text" class="form-control" placeholder="Search by Audience Name or Description ..." v-model="search_key" @input="searchAudience" aria-label="Search by Audience Name or Description ...">
                            
                        </div>
                        <div class="flex-grow-1 position-relative" style="max-height: 60vh; overflow-y:auto;">
                            <div v-if="filtered_audience_list.length === 0" class="card long-card disabled">
                                <div class="card-body long-card__body row">
                                    <div class="long-card__content">
                                        <h4 class="mb-1">No Audiences Found</h4>
                                    </div>
                                </div>
                            </div>
                            <div v-else v-for="audience in paginated_audience_list" :key="audience.id" class="content-card mb-3 cursor-move" 
                                draggable="true"
                                @dragstart="onDragStart(audience, $event)">
                                <div class="content-card-body">
                                    <h4>{{ audience.name }}</h4>
                                    <p class="small mb-0" v-if="audience.value_prop">{{ audience.value_prop }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Pagination controls -->
                    <div class="card-footer d-flex justify-content-between align-items-center">
                        <div class="ps-2">
                            <select class="form-select form-select-sm" v-model="num_per_page" @change="reloadPagination" aria-label="Num Per Page">
                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                            </select>
                        </div>
                        <div>
                            <button class="btn btn-secondary btn-icon-only-md me-1" @click="prevPage" :disabled="page_index===0">
                                <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Prev">
                            </button>
                            <span>{{ page_index+1 }} / {{ page_count }}</span>
                            <button class="btn btn-secondary btn-icon-only-md ms-1" @click="nextPage" :disabled="page_index>=page_count-1">
                                <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Selected List -->
            <div class="col-lg-6 col-12 mt-3 mt-lg-0">
                <div class="card h-100 d-flex flex-column" @dragover.prevent @drop="onDropRight">
                    <div class="card-body pb-0">
                        <h3>Selected Audiences ({{ selectedAudiencesInternal.length }})</h3>
                        <div class="flex-grow-1 position-relative" style="max-height: 80vh; overflow-y:auto;">
                            <div v-if="selectedAudiencesInternal.length === 0" class="card long-card disabled mt-3">
                                <div class="card-body long-card__body row">
                                    <div class="long-card__content">
                                        <h4 class="mb-1">No Audiences Selected</h4>
                                        <p>Select an audience from the list on the left.</p>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="scroll-view d-flex flex-column flex-shrink-1 pe-3 pt-3" style="max-height: 100%;">
                                <draggable v-model="selectedAudiencesInternal" handle=".handle" item-key="id">
                                    <template #item="{ element }">
                                    <div class="card long-card mb-3 cursor-move" draggable="true" @dragstart="onDragStartSelected(element, $event)">
                                        <div class="card-body long-card__body row align-items-center">
                                            <div class="col-1">
                                                <div class="btn btn-secondary btn-icon-only-md handle">
                                                    <img src="/img/icons/avid-icons/vertical-dots.svg" alt="drag" class="icon">
                                                </div>
                                            </div>
                                            <div class="col-8">
                                                <h4 class="mb-0">{{ element.name }}</h4>
                                            </div>
                                            <div class="col-2 d-flex justify-content-end">
                                                <button type="button" class="btn btn-warning btn-icon-only-md" @click="removeAudienceSelection(element)">
                                                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <link rel="preload" href="/img/icons/avid-icons/vertical-dots.svg" as="image" type="image/svg+xml" />
    </div>
</template>

<script>
import { defineComponent, watch, ref } from 'vue';
import draggable from 'vuedraggable';

export default defineComponent({
    name: 'AudienceSelector',
    components: { draggable },
    props: {
        availableAudiences: {
            type: Array,
            default: () => []
        },
        modelValue: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        // Internal reactive copies
        const search_key = ref('');
        const num_per_page = ref(10);
        const num_per_page_options = [10, 20, 30, 50, 100];
        const page_index = ref(0);
        const filtered_audience_list = ref([]);

        const selectedAudiencesInternal = ref([...props.modelValue]);

        // Watch the v-model and update internal list
        watch(() => props.modelValue, (val) => {
            selectedAudiencesInternal.value = [...val];
        }, { immediate: true });

        // Emit when internal list changes (but avoid circular updates)
        watch(selectedAudiencesInternal, (val) => {
            // Only emit if the values are actually different to prevent circular updates
            const currentIds = val.map(a => a.id).sort();
            const propIds = props.modelValue.map(a => a.id).sort();
            if (JSON.stringify(currentIds) !== JSON.stringify(propIds)) {
                emit('update:modelValue', val);
            }
        }, { deep: true });

        // Initial filter
        const resetFilteredAudiences = () => {
            filtered_audience_list.value = props.availableAudiences.filter(aud => !selectedAudiencesInternal.value.some(sel => sel.id === aud.id)).sort((a,b)=> a.name.localeCompare(b.name));
            reloadPagination();
        };

        const searchAudience = () => {
            let list = [];
            if(search_key.value === ''){
                list = props.availableAudiences;
            } else {
                const key = search_key.value.toLowerCase();
                list = props.availableAudiences.filter(aud => aud.name.toLowerCase().includes(key) || (aud.value_prop && aud.value_prop.toLowerCase().includes(key)));
            }
            list = list.filter(aud => !selectedAudiencesInternal.value.some(sel => sel.id === aud.id));
            filtered_audience_list.value = list.sort((a,b)=> a.name.localeCompare(b.name));
            page_index.value = 0;
            reloadPagination();
        };

        const page_count = ref(1);
        const paginated_audience_list = ref([]);

        const reloadPagination = () => {
            page_count.value = Math.max(1, Math.ceil(filtered_audience_list.value.length / num_per_page.value));
            getPaginated();
        };

        const getPaginated = () => {
            const start = page_index.value * num_per_page.value;
            paginated_audience_list.value = filtered_audience_list.value.slice(start, start + num_per_page.value);
        };

        const prevPage = () => { if(page_index.value>0){ page_index.value--; getPaginated(); } };
        const nextPage = () => { if(page_index.value < page_count.value-1){ page_index.value++; getPaginated(); } };

        const addAudienceSelection = (audience) => {
            selectedAudiencesInternal.value.push(audience);
            resetFilteredAudiences();
        };

        const removeAudienceSelection = (audience) => {
            const idx = selectedAudiencesInternal.value.findIndex(a => a.id === audience.id);
            if(idx>=0) selectedAudiencesInternal.value.splice(idx,1);
            resetFilteredAudiences();
        };

        const onDragStart = (aud, event) => {
            event.dataTransfer.setData('audienceId', aud.id);
        };
        const onDragStartSelected = (aud, event) => {
            event.dataTransfer.setData('audienceId', aud.id);
        };

        const onDropRight = (e) => {
            const id = parseInt(e.dataTransfer.getData('audienceId'));
            if(!id) return;
            const already = selectedAudiencesInternal.value.some(a=>a.id===id);
            if(already) return;
            const aud = props.availableAudiences.find(a=>a.id===id);
            if(aud){
                addAudienceSelection(aud);
            }
        };

        // Initialize
        resetFilteredAudiences();

        return {
            search_key,
            num_per_page,
            num_per_page_options,
            page_index,
            page_count,
            filtered_audience_list,
            paginated_audience_list,
            selectedAudiencesInternal,
            searchAudience,
            reloadPagination,
            prevPage,
            nextPage,
            addAudienceSelection,
            removeAudienceSelection,
            onDragStart,
            onDropRight,
            onDragStartSelected
        };
    }
});
</script>

<style scoped>
.search-box i{
    position:absolute;
    right:12px;
    top:50%;
    transform:translateY(-50%);
    pointer-events:none;
    color:#6c757d;
}
</style>
